import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Waves, Sun, Droplets, ArrowRight } from 'lucide-react';

const UCSanDiego: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "UC San Diego";

    return (
        <div className="min-h-screen bg-white font-sans text-[#182B49] selection:bg-[#FFCD00] selection:text-[#182B49]">
             {/* Top Utility */}
             <div className="bg-[#182B49] text-white py-2 px-6 flex justify-end gap-6 text-[11px] font-bold uppercase tracking-wider">
                 <a href="#" className="hover:text-[#FFCD00]">Faculty/Staff</a>
                 <a href="#" className="hover:text-[#FFCD00]">Alumni</a>
                 <a href="#" className="hover:text-[#FFCD00]">Students</a>
                 <a href="#" className="hover:text-[#FFCD00]">Parents</a>
             </div>

            {/* Nav */}
            <nav className="bg-white py-5 px-6 md:px-12 border-b border-[#00629B]">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="text-2xl font-bold uppercase tracking-tight text-[#182B49] leading-none">{schoolName}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#182B49] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#00629B] transition-colors">About</a>
                        <a href="#" className="hover:text-[#00629B] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#00629B] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#00629B] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#00629B] transition-colors">Campus Life</a>
                    </div>
                     
                     <div className="flex gap-4 text-[#182B49]">
                         <Search className="cursor-pointer hover:text-[#00629B]" size={22} />
                         <Menu className="cursor-pointer lg:hidden hover:text-[#00629B]" size={24} />
                     </div>
                </div>
            </nav>

            {/* Hero - Geisel Library / Coastal */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1596245344485-6490696773df?q=80&w=2070&auto=format&fit=crop" 
                    alt="Geisel Library" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Gradient Blue Overlay */}
                 <div className="absolute inset-0 bg-gradient-to-l from-[#00C6D7]/30 to-[#182B49]/80 mix-blend-multiply"></div>

                 <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                     <div className="max-w-2xl text-white">
                         <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                             Break Things <br/>
                             <span className="text-[#FFCD00]">Better.</span>
                         </h1>
                         <p className="text-xl md:text-2xl mb-12 font-light text-gray-100">
                             At {schoolName}, we invite you to challenge the status quo, look deeper, and drive change.
                         </p>
                         <Button className="bg-[#FFCD00] hover:bg-[#FFB81C] text-[#182B49] font-bold uppercase tracking-widest px-10 h-14 rounded-full">
                             Start Your Application
                         </Button>
                     </div>
                 </div>
            </header>

            {/* Icons - Elements */}
            <section className="bg-[#182B49] text-white py-20 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div className="p-6 border border-[#00629B] rounded-lg hover:bg-[#00629B]/20 transition-colors cursor-pointer">
                         <Waves size={48} className="text-[#00C6D7] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4">Ocean & Earth</h3>
                         <p className="text-gray-300">Scripps Institution of Oceanography leads the world in earth science research.</p>
                     </div>
                     <div className="p-6 border border-[#00629B] rounded-lg hover:bg-[#00629B]/20 transition-colors cursor-pointer">
                         <Sun size={48} className="text-[#FFCD00] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4">Energy & Innovation</h3>
                         <p className="text-gray-300">Pioneering sustainable solutions for a cleaner, brighter future.</p>
                     </div>
                     <div className="p-6 border border-[#00629B] rounded-lg hover:bg-[#00629B]/20 transition-colors cursor-pointer">
                         <Droplets size={48} className="text-[#00C6D7] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4">Health & Medicine</h3>
                         <p className="text-gray-300">Top-ranked medical center transforming patient care.</p>
                     </div>
                </div>
            </section>

             {/* Dynamic Content */}
             <section className="bg-[#f0f0f0] py-24 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto">
                     <h2 className="text-3xl font-bold text-[#182B49] mb-12 flex items-center gap-4">
                         Triton Stories <ArrowRight className="text-[#FFCD00]"/>
                     </h2>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                         <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all group">
                             <div className="h-56 overflow-hidden">
                                 <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="Tech" />
                             </div>
                             <div className="p-8">
                                 <h3 className="text-xl font-bold text-[#182B49] mb-4">Halıcıoğlu Data Science Institute expands</h3>
                                 <a href="#" className="text-[#00629B] font-bold uppercase text-xs tracking-widest hover:underline">Read Article</a>
                             </div>
                         </div>
                          <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all group">
                             <div className="h-56 overflow-hidden">
                                 <img src="https://images.unsplash.com/photo-1544928147-79a2af1f9850?q=80&w=2066&auto=format&fit=crop" className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="People" />
                             </div>
                             <div className="p-8">
                                 <h3 className="text-xl font-bold text-[#182B49] mb-4">{schoolName} welcomes most diverse class ever</h3>
                                 <a href="#" className="text-[#00629B] font-bold uppercase text-xs tracking-widest hover:underline">Read Article</a>
                             </div>
                         </div>
                          <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all group">
                             <div className="h-56 overflow-hidden">
                                 <img src="https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?q=80&w=2034&auto=format&fit=crop" className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="Arts" />
                             </div>
                             <div className="p-8">
                                 <h3 className="text-xl font-bold text-[#182B49] mb-4">La Jolla Playhouse features student productions</h3>
                                 <a href="#" className="text-[#00629B] font-bold uppercase text-xs tracking-widest hover:underline">Read Article</a>
                             </div>
                         </div>
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-white text-[#182B49] py-16 px-6 md:px-12 border-t-4 border-[#FFCD00]">
                 <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12 text-sm">
                      <div className="max-w-xs">
                          <h4 className="font-bold text-2xl uppercase tracking-tighter mb-4">{schoolName}</h4>
                          <p className="text-gray-600 mb-6">
                              9500 Gilman Drive <br/>
                              La Jolla, CA 92093 <br/>
                              (858) 534-2230
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold text-xs uppercase tracking-widest">
                          <ul className="space-y-4">
                              <li><a href="#" className="hover:text-[#00629B]">Jobs</a></li>
                              <li><a href="#" className="hover:text-[#00629B]">Giving</a></li>
                              <li><a href="#" className="hover:text-[#00629B]">Contact</a></li>
                          </ul>
                           <ul className="space-y-4">
                              <li><a href="#" className="hover:text-[#00629B]">Emergency</a></li>
                              <li><a href="#" className="hover:text-[#00629B]">Accessibility</a></li>
                              <li><a href="#" className="hover:text-[#00629B]">Privacy</a></li>
                          </ul>
                      </div>
                 </div>
                 <div className="max-w-[1200px] mx-auto mt-12 pt-8 border-t border-gray-100 text-center text-xs text-gray-400">
                     © 2026 Regents of the University of California
                 </div>
             </footer>
        </div>
    );
};

export default UCSanDiego;
